import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { InputError } from '@/components/common/InputError';
import { ScheduleIcon } from '@/components/insurance/components/FlightFieldIcons';
import { FlightMaskedInput } from '../FlightMaskedInput';
import { useStyles } from './styles';
const HOURS = Array.from({ length: 24 }).map((_, index) => index);
const MINUTES = Array.from({ length: 60 }).map((_, index) => index);
const WHEEL_ITEM_HEIGHT = 30;
const WHEEL_ITEM_HEIGHT_TOUCH = 34;
const WHEEL_ITEM_GAP = 2;
const TOUCH_MEDIA_QUERY = '(max-width: 768px)';
const TIME_REGEXP = /^([01]\d|2[0-3]):([0-5]\d)$/;
const TIME_MASK_VALUE = 'ЧЧ:ММ';
const TIME_MASK_REPLACEMENT = { Ч: /\d/, М: /\d/ };
const postfixAnimation = {
    variants: {
        hidden: { opacity: 0, scale: 0.3 },
        visible: { opacity: 1, scale: 1 },
    },
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    transition: { duration: 0.08, bounce: 40 },
};
const getFormattedTime = ({ hours, minutes }) => `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
const parseFlightTime = (value) => {
    if (!TIME_REGEXP.test(value)) {
        return null;
    }
    const [hours, minutes] = value.split(':').map(Number);
    return { hours, minutes };
};
const getTimeParts = (value) => {
    if (value.includes(':')) {
        const [rawHours = '', rawMinutes = ''] = value.split(':');
        return {
            hours: rawHours.replace(/\D/g, '').slice(0, 2),
            minutes: rawMinutes.replace(/\D/g, '').slice(0, 2),
        };
    }
    const digits = value.replace(/\D/g, '').slice(0, 4);
    return {
        hours: digits.slice(0, 2),
        minutes: digits.slice(2, 4),
    };
};
const getMaskedDisplayValue = (value) => {
    const { hours, minutes } = getTimeParts(value);
    const hoursDisplay = hours ? hours.padStart(2, '0') : 'ЧЧ';
    const minutesDisplay = minutes ? minutes.padStart(2, '0') : 'ММ';
    return `${hoursDisplay}:${minutesDisplay}`;
};
const getTimeInputValue = (value, isEditing) => {
    if (!value) {
        return '';
    }
    if (isEditing || !parseFlightTime(value)) {
        return getMaskedDisplayValue(value);
    }
    return value;
};
const getStoredTimeValue = (maskedValue) => {
    const { hours, minutes } = getTimeParts(maskedValue);
    if (!hours && !minutes) {
        return '';
    }
    return minutes ? `${hours}:${minutes}` : hours;
};
const getSegmentFromSelection = (position) => position <= 2 ? 'hours' : 'minutes';
const getInputTextWidth = (input, text) => {
    const styles = window.getComputedStyle(input);
    const context = document.createElement('canvas').getContext('2d');
    const letterSpacing = Number.parseFloat(styles.letterSpacing) || 0;
    if (!context) {
        return 0;
    }
    context.font = `${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
    return (context.measureText(text).width +
        Math.max(0, text.length - 1) * letterSpacing);
};
const getSegmentFromClickPosition = (input, clientX) => {
    const { left } = input.getBoundingClientRect();
    const displayValue = (input.value || TIME_MASK_VALUE).padEnd(TIME_MASK_VALUE.length, ' ');
    const position = Math.max(0, clientX - left);
    const hoursMinutesBoundary = (getInputTextWidth(input, displayValue.slice(0, 2)) +
        getInputTextWidth(input, displayValue.slice(0, 3))) /
        2;
    return position < hoursMinutesBoundary ? 'hours' : 'minutes';
};
const getSegmentRange = (segment) => segment === 'hours' ? { start: 0, end: 2 } : { start: 3, end: 5 };
const getSegmentMaxValue = (segment) => segment === 'hours' ? HOURS.length - 1 : MINUTES.length - 1;
const setDisplaySegment = (value, segment, segmentValue) => {
    const nextValue = getMaskedDisplayValue(value).split('');
    const { start } = getSegmentRange(segment);
    const nextSegmentValue = segmentValue ||
        (segment === 'hours'
            ? TIME_MASK_VALUE.slice(0, 2)
            : TIME_MASK_VALUE.slice(3, 5));
    nextValue[start] = nextSegmentValue[0];
    nextValue[start + 1] = nextSegmentValue[1];
    return nextValue.join('');
};
const CheckIcon = () => (_jsx("svg", { xmlns: "http://www.w3.org/2000/svg", height: "24", width: "24", viewBox: "0 -960 960 960", "aria-hidden": "true", children: _jsx("path", { d: "M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z" }) }));
export const FlightTimePickerInput = memo(({ value, label, placeholder, error, onChange, onBlur, }) => {
    const classes = useStyles();
    const rootRef = useRef(null);
    const inputRef = useRef(null);
    const hoursRef = useRef(null);
    const minutesRef = useRef(null);
    const isOpenRef = useRef(false);
    const shouldSelectInitialSegmentRef = useRef(true);
    const [isOpen, setOpen] = useState(false);
    const [isEditing, setEditing] = useState(false);
    const [displayValue, setDisplayValue] = useState(value);
    const displayValueRef = useRef(displayValue);
    const pendingSegmentInputRef = useRef(null);
    const pointerSegmentRef = useRef(null);
    const pendingSegmentSelectAfterRenderRef = useRef(null);
    const segmentSelectAfterRenderClearTimeoutRef = useRef(null);
    const segmentSelectTimeoutRef = useRef(null);
    const segmentSelectFrameRef = useRef(null);
    const segmentSelectSecondFrameRef = useRef(null);
    const parsedValue = useMemo(() => parseFlightTime(value), [value]);
    const hasSelectedTimeRef = useRef(Boolean(parsedValue));
    const [selectedTime, setSelectedTime] = useState(parsedValue || { hours: 0, minutes: 0 });
    const selectedTimeRef = useRef(selectedTime);
    const scrollWheelToIndex = (element, index, behavior = 'smooth') => {
        if (!element) {
            return;
        }
        const itemHeight = window.matchMedia(TOUCH_MEDIA_QUERY).matches
            ? WHEEL_ITEM_HEIGHT_TOUCH
            : WHEEL_ITEM_HEIGHT;
        element.scrollTo({
            top: index * (itemHeight + WHEEL_ITEM_GAP),
            behavior,
        });
    };
    const clearQueuedTimeSegmentSelect = () => {
        pendingSegmentSelectAfterRenderRef.current = null;
        if (segmentSelectAfterRenderClearTimeoutRef.current !== null) {
            window.clearTimeout(segmentSelectAfterRenderClearTimeoutRef.current);
            segmentSelectAfterRenderClearTimeoutRef.current = null;
        }
        if (segmentSelectTimeoutRef.current !== null) {
            window.clearTimeout(segmentSelectTimeoutRef.current);
            segmentSelectTimeoutRef.current = null;
        }
        if (segmentSelectFrameRef.current !== null) {
            window.cancelAnimationFrame(segmentSelectFrameRef.current);
            segmentSelectFrameRef.current = null;
        }
        if (segmentSelectSecondFrameRef.current !== null) {
            window.cancelAnimationFrame(segmentSelectSecondFrameRef.current);
            segmentSelectSecondFrameRef.current = null;
        }
    };
    const applyTimeSegmentSelection = ({ segment, targetInput, shouldRestoreFocus, }) => {
        if (inputRef.current !== targetInput) {
            return;
        }
        if (document.activeElement !== targetInput) {
            if (!shouldRestoreFocus) {
                return;
            }
            targetInput.focus({ preventScroll: true });
        }
        if (document.activeElement !== targetInput) {
            return;
        }
        const { start, end } = getSegmentRange(segment);
        targetInput.setSelectionRange(start, end);
    };
    const selectTimeSegment = (segment, targetInput = inputRef.current, shouldRestoreFocus = false) => {
        if (!targetInput) {
            return;
        }
        const pendingSegmentSelect = {
            segment,
            targetInput,
            shouldRestoreFocus,
        };
        clearQueuedTimeSegmentSelect();
        segmentSelectFrameRef.current = window.requestAnimationFrame(() => {
            segmentSelectFrameRef.current = null;
            applyTimeSegmentSelection(pendingSegmentSelect);
            segmentSelectSecondFrameRef.current =
                window.requestAnimationFrame(() => {
                    segmentSelectSecondFrameRef.current = null;
                    applyTimeSegmentSelection(pendingSegmentSelect);
                });
        });
    };
    const queueTimeSegmentSelect = (segment, targetInput = inputRef.current, shouldRestoreFocus = false) => {
        if (!targetInput) {
            return;
        }
        if (segmentSelectTimeoutRef.current !== null) {
            window.clearTimeout(segmentSelectTimeoutRef.current);
        }
        segmentSelectTimeoutRef.current = window.setTimeout(() => {
            segmentSelectTimeoutRef.current = null;
            selectTimeSegment(segment, targetInput, shouldRestoreFocus);
        }, 0);
    };
    const selectTimeSegmentAfterRender = (segment, targetInput) => {
        if (segmentSelectAfterRenderClearTimeoutRef.current !== null) {
            window.clearTimeout(segmentSelectAfterRenderClearTimeoutRef.current);
        }
        pendingSegmentSelectAfterRenderRef.current = {
            segment,
            targetInput,
            shouldRestoreFocus: true,
        };
        segmentSelectAfterRenderClearTimeoutRef.current = window.setTimeout(() => {
            pendingSegmentSelectAfterRenderRef.current = null;
            segmentSelectAfterRenderClearTimeoutRef.current = null;
        }, 180);
    };
    const selectTimeSegmentFromInput = (input, clientX) => {
        return typeof clientX === 'number'
            ? getSegmentFromClickPosition(input, clientX)
            : getSegmentFromSelection(input.selectionStart ?? 0);
    };
    const syncDisplayValue = (nextDisplayValue) => {
        const nextValue = getStoredTimeValue(nextDisplayValue);
        const nextParsedValue = parseFlightTime(nextValue);
        displayValueRef.current = nextDisplayValue;
        setDisplayValue(nextDisplayValue);
        onChange(nextValue);
        if (!nextValue || !nextParsedValue) {
            hasSelectedTimeRef.current = false;
            return;
        }
        hasSelectedTimeRef.current = true;
        selectedTimeRef.current = nextParsedValue;
        setSelectedTime(nextParsedValue);
        scrollWheelToIndex(hoursRef.current, nextParsedValue.hours, 'auto');
        scrollWheelToIndex(minutesRef.current, nextParsedValue.minutes, 'auto');
    };
    const clearTimeSegment = (segment, targetInput) => {
        const nextDisplayValue = setDisplaySegment(displayValueRef.current || getMaskedDisplayValue(value), segment, '');
        pendingSegmentInputRef.current = null;
        selectTimeSegmentAfterRender(segment, targetInput);
        syncDisplayValue(nextDisplayValue);
    };
    const applySmartSegmentDigit = (segment, digit, targetInput = inputRef.current) => {
        const pendingSegmentInput = pendingSegmentInputRef.current;
        let nextSegment = segment;
        let nextSegmentValue = `0${digit}`;
        let nextSelectedSegment = segment;
        if (pendingSegmentInput?.segment === segment) {
            const candidateValue = `${pendingSegmentInput.digit}${digit}`;
            const canCompleteSegment = Number(candidateValue) <= getSegmentMaxValue(segment);
            if (canCompleteSegment) {
                nextSegmentValue = candidateValue;
                nextSelectedSegment =
                    segment === 'hours' ? 'minutes' : 'minutes';
                pendingSegmentInputRef.current = null;
            }
            else if (segment === 'hours') {
                nextSegment = 'minutes';
                nextSelectedSegment = 'minutes';
                pendingSegmentInputRef.current = {
                    segment: 'minutes',
                    digit,
                };
            }
            else {
                pendingSegmentInputRef.current = { segment, digit };
            }
        }
        else {
            pendingSegmentInputRef.current = { segment, digit };
        }
        const nextDisplayValue = setDisplaySegment(displayValueRef.current || getMaskedDisplayValue(value), nextSegment, nextSegmentValue);
        if (targetInput) {
            selectTimeSegmentAfterRender(nextSelectedSegment, targetInput);
        }
        syncDisplayValue(nextDisplayValue);
    };
    const commitTime = useCallback((time) => {
        const nextValue = getFormattedTime(time);
        const nextDisplayValue = getMaskedDisplayValue(nextValue);
        displayValueRef.current = nextDisplayValue;
        setDisplayValue(nextDisplayValue);
        if (nextValue !== value) {
            onChange(nextValue);
        }
    }, [onChange, value]);
    useLayoutEffect(() => {
        const pendingSegmentSelect = pendingSegmentSelectAfterRenderRef.current;
        if (!pendingSegmentSelect) {
            return;
        }
        applyTimeSegmentSelection(pendingSegmentSelect);
        if (segmentSelectFrameRef.current !== null) {
            window.cancelAnimationFrame(segmentSelectFrameRef.current);
        }
        if (segmentSelectSecondFrameRef.current !== null) {
            window.cancelAnimationFrame(segmentSelectSecondFrameRef.current);
        }
        segmentSelectFrameRef.current = window.requestAnimationFrame(() => {
            segmentSelectFrameRef.current = null;
            applyTimeSegmentSelection(pendingSegmentSelect);
            segmentSelectSecondFrameRef.current =
                window.requestAnimationFrame(() => {
                    segmentSelectSecondFrameRef.current = null;
                    applyTimeSegmentSelection(pendingSegmentSelect);
                });
        });
    });
    useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }
        if (isEditing && value && !parsedValue) {
            return;
        }
        hasSelectedTimeRef.current = Boolean(parsedValue);
        if (parsedValue) {
            selectedTimeRef.current = parsedValue;
            setSelectedTime(parsedValue);
            scrollWheelToIndex(hoursRef.current, parsedValue.hours, 'auto');
            scrollWheelToIndex(minutesRef.current, parsedValue.minutes, 'auto');
            return;
        }
        const { hours, minutes } = getTimeParts(value);
        if (hours.length === 2) {
            scrollWheelToIndex(hoursRef.current, Number(hours), 'auto');
        }
        if (minutes.length === 2) {
            scrollWheelToIndex(minutesRef.current, Number(minutes), 'auto');
        }
    }, [isEditing, isOpen, parsedValue, value]);
    useEffect(() => {
        if (parsedValue && !isOpen) {
            hasSelectedTimeRef.current = true;
            selectedTimeRef.current = parsedValue;
            setSelectedTime(parsedValue);
        }
    }, [isOpen, parsedValue]);
    useEffect(() => {
        if (!isEditing) {
            displayValueRef.current = value;
            setDisplayValue(value);
        }
    }, [isEditing, value]);
    useEffect(() => {
        isOpenRef.current = isOpen;
    }, [isOpen]);
    useEffect(() => {
        const handlePointerDown = (event) => {
            if (isOpenRef.current &&
                rootRef.current &&
                !rootRef.current.contains(event.target)) {
                clearQueuedTimeSegmentSelect();
                if (hasSelectedTimeRef.current) {
                    commitTime(selectedTimeRef.current);
                }
                onBlur();
                pendingSegmentInputRef.current = null;
                setEditing(false);
                setOpen(false);
            }
        };
        document.addEventListener('pointerdown', handlePointerDown, true);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true);
            clearQueuedTimeSegmentSelect();
        };
    }, [commitTime, onBlur]);
    const setTimeSegment = (segment, nextValue) => {
        const maxValue = segment === 'hours' ? HOURS.length - 1 : MINUTES.length - 1;
        const normalizedValue = Math.max(0, Math.min(maxValue, nextValue));
        const nextDisplayValue = setDisplaySegment(displayValueRef.current || getMaskedDisplayValue(value), segment, String(normalizedValue).padStart(2, '0'));
        pendingSegmentInputRef.current = null;
        syncDisplayValue(nextDisplayValue);
        scrollWheelToIndex(segment === 'hours' ? hoursRef.current : minutesRef.current, normalizedValue);
        if (document.activeElement === inputRef.current) {
            queueTimeSegmentSelect(segment);
        }
    };
    const handleWheelItemClick = (type, nextValue) => {
        setTimeSegment(type, nextValue);
    };
    const handleInputFocus = (event) => {
        const nextDisplayValue = getMaskedDisplayValue(value);
        pendingSegmentInputRef.current = null;
        displayValueRef.current = nextDisplayValue;
        setEditing(true);
        setDisplayValue(nextDisplayValue);
        if (shouldSelectInitialSegmentRef.current) {
            selectTimeSegment('hours', event.currentTarget);
        }
    };
    const handleInputBlur = () => {
        clearQueuedTimeSegmentSelect();
        pendingSegmentInputRef.current = null;
        shouldSelectInitialSegmentRef.current = true;
        setEditing(false);
        setOpen(false);
        onBlur();
    };
    const handleInputPointerDown = (event) => {
        const wasFocused = document.activeElement === event.currentTarget;
        clearQueuedTimeSegmentSelect();
        shouldSelectInitialSegmentRef.current = false;
        pointerSegmentRef.current =
            event.pointerType !== 'mouse' || !wasFocused
                ? selectTimeSegmentFromInput(event.currentTarget, event.clientX)
                : null;
    };
    const handleInputPointerUp = (event) => {
        pendingSegmentInputRef.current = null;
        if (pointerSegmentRef.current) {
            queueTimeSegmentSelect(pointerSegmentRef.current, event.currentTarget);
        }
        shouldSelectInitialSegmentRef.current = true;
    };
    const handleInputClick = (event) => {
        const input = event.currentTarget;
        const segment = pointerSegmentRef.current;
        pendingSegmentInputRef.current = null;
        pointerSegmentRef.current = null;
        if (segment) {
            queueTimeSegmentSelect(segment, input);
            return;
        }
        queueTimeSegmentSelect(selectTimeSegmentFromInput(input), input);
    };
    const handleInputChange = (event) => {
        pendingSegmentInputRef.current = null;
        syncDisplayValue(event.target.value);
    };
    const handleInputKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            setOpen((current) => !current);
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            setEditing(false);
            setOpen(false);
            return;
        }
        if (event.key === 'Backspace' || event.key === 'Delete') {
            event.preventDefault();
            clearTimeSegment(getSegmentFromSelection(event.currentTarget.selectionStart ?? 0), event.currentTarget);
            return;
        }
        if (event.key === ':') {
            event.preventDefault();
            pendingSegmentInputRef.current = null;
            selectTimeSegment('minutes');
            return;
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            pendingSegmentInputRef.current = null;
            selectTimeSegment(event.key === 'ArrowLeft' ? 'hours' : 'minutes');
            return;
        }
        if (/^\d$/.test(event.key)) {
            event.preventDefault();
            applySmartSegmentDigit(getSegmentFromSelection(event.currentTarget.selectionStart ?? 0), event.key, event.currentTarget);
        }
    };
    const handleTimeButtonPointerDown = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };
    const handleTimeButtonClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (isOpen) {
            if (hasSelectedTimeRef.current) {
                commitTime(selectedTimeRef.current);
            }
            onBlur();
            pendingSegmentInputRef.current = null;
            setEditing(false);
            setOpen(false);
            inputRef.current?.blur();
            return;
        }
        setOpen(true);
    };
    const inputValue = isEditing
        ? displayValue
        : getTimeInputValue(value, isEditing);
    const selectedDisplayParts = getTimeParts(displayValue || value);
    const selectedHour = selectedDisplayParts.hours.length === 2
        ? Number(selectedDisplayParts.hours)
        : null;
    const selectedMinute = selectedDisplayParts.minutes.length === 2
        ? Number(selectedDisplayParts.minutes)
        : null;
    const renderWheel = (type, items, selectedItem, ref) => (_jsx("div", { className: classes.wheelColumn, children: _jsx("div", { className: classes.wheel, ref: ref, tabIndex: -1, children: items.map((item) => (_jsx("button", { type: "button", className: classes.wheelItem, "data-selected": selectedItem === item, onPointerDown: (event) => event.preventDefault(), onClick: () => handleWheelItemClick(type, item), tabIndex: -1, children: String(item).padStart(2, '0') }, item))) }) }));
    return (_jsxs("div", { className: classes.root, "data-open": isOpen, ref: rootRef, children: [_jsxs("div", { className: classes.inputWrapper, children: [_jsx(FlightMaskedInput, { inputRef: inputRef, value: inputValue, label: label, placeholder: placeholder, mask: TIME_MASK_VALUE, replacement: TIME_MASK_REPLACEMENT, showMask: isEditing, separate: true, invalid: Boolean(error), isActive: isOpen || isEditing, inputMode: "numeric", onFocus: handleInputFocus, onBlur: handleInputBlur, onClick: handleInputClick, onPointerDown: handleInputPointerDown, onPointerUp: handleInputPointerUp, onChange: handleInputChange, onKeyDown: handleInputKeyDown, postfix: _jsx("button", { type: "button", className: classes.timeButton, "data-open": isOpen, "aria-label": isOpen
                                ? 'Закрыть выбор времени'
                                : 'Открыть выбор времени', onPointerDown: handleTimeButtonPointerDown, onClick: handleTimeButtonClick, children: _jsx(AnimatePresence, { initial: false, children: !isOpen ? (_createElement(motion.div, { ...postfixAnimation, className: classes.postfixIcon, key: "clock" },
                                    _jsx(ScheduleIcon, {}))) : (_createElement(motion.div, { ...postfixAnimation, className: classes.postfixConfirm, key: "confirm" },
                                    _jsx(CheckIcon, {}))) }) }) }), _jsx(AnimatePresence, { children: isOpen && (_jsxs(motion.div, { className: classes.popover, initial: { opacity: 0, y: -6, scale: 0.98 }, animate: { opacity: 1, y: 0, scale: 1 }, exit: { opacity: 0, y: -6, scale: 0.98 }, transition: { duration: 0.16 }, children: [_jsxs("div", { className: classes.wheelHeaders, children: [_jsx("span", { children: "\u0427\u0430\u0441\u044B" }), _jsx("span", { children: "\u041C\u0438\u043D\u0443\u0442\u044B" })] }), _jsxs("div", { className: classes.wheels, children: [renderWheel('hours', HOURS, selectedHour, hoursRef), renderWheel('minutes', MINUTES, selectedMinute, minutesRef)] })] })) })] }), _jsx(InputError, { error: error })] }));
});
